/**
 * Open-redirect guard for the post-login destination.
 *
 * Lives in `lib/auth` rather than inside `app/auth/login/route.ts` because a Next.js
 * route module may only export HTTP method handlers and `config` — a helper exported
 * from `route.ts` breaks the build. Extracting it keeps the guard testable as a pure
 * function of `(raw, origin) -> path`.
 *
 * Two guards, both needed. The `URL` constructor handles payloads that change
 * authority: `//evil.com`, `/\evil.com` and `https:/\evil.com` all normalise to a
 * foreign `origin` and the comparison below rejects them. It cannot handle a
 * payload that stays same-origin while carrying a leading `//` in its path —
 * `http://localhost:3000//evil.com/x` passes the origin check — and the login
 * route re-parses whatever this returns, so the `//`-collapse at the end is what
 * stops that one.
 */
export function normalizeReturnTo(raw: string | null, origin: string): string {
  if (!raw) return "/";

  try {
    let url: URL;

    if (raw.startsWith("http://") || raw.startsWith("https://")) {
      url = new URL(raw);
    } else {
      url = new URL(raw, origin);
    }

    if (url.origin !== origin) {
      console.warn(`Blocked open redirect attempt to: ${url.origin}`);

      return "/";
    }

    const path = url.pathname + url.search;

    if (
      path.startsWith("/login") ||
      path.startsWith("/auth/login") ||
      path.startsWith("/refresh") ||
      path.startsWith("/api/refresh") ||
      path.startsWith("/api/login")
    ) {
      return "/";
    }

    return path.replace(/^\/\//, "/") || "/";
  } catch (e) {
    return "/";
  }
}

/**
 * The `?error=` value that says the backend refused the *session*, not the password.
 * `app/refresh/route.ts` and `proxy.ts` already answer an API path with this code in the body;
 * this is the same fact carried in the address, where the login page and the middleware can
 * both read it.
 */
export const SESSION_EXPIRED_ERROR = "session_expired";

/**
 * The login page, pre-blamed on the session and remembering the page the visitor asked for.
 *
 * The marker is what keeps this redirect from looping: `proxy.ts` bounces an unexpired
 * `accessToken` away from `/login` back to `/`, and the token that the backend just refused is
 * exactly such a token. The middleware reads the marker, drops that cookie instead of bouncing,
 * and leaves `refreshToken` for the refresh path to clear when a refresh attempt actually fails.
 */
export function loginSessionExpiredHref(callbackUrl: string): string {
  const params = new URLSearchParams({
    error: SESSION_EXPIRED_ERROR,
    callbackUrl,
  });

  return `/login?${params.toString()}`;
}
